/**
 * Plantilla de horarios de UNA campaña: filas = tienda, columnas = fecha.
 *
 * Pedido del usuario (2026-08-15): *"me gustaría tener acá una parte donde pueda subir una
 * plantilla […] cada fila una tienda y las columnas una fecha, algo fácil de usar"*.
 *
 * POR QUÉ LA PLANTILLA SE DESCARGA Y NO SE ACEPTA CUALQUIER EXCEL
 * El importador general (`importProgramacion.ts`) lee el formato que manda el cliente, con toda la
 * tolerancia que eso exige. Acá el caso es otro: la campaña YA existe, con sus fechas y sus
 * tiendas conocidas. Generando nosotros la grilla —tiendas y fechas ya escritas, la persona solo
 * marca— desaparece la clase entera de fallos de emparejamiento y de calendario desalineado. Lo
 * flexible tiene su lugar y es el intérprete; esto es la vía rápida y segura.
 *
 * LA REGLA QUE NO SE NEGOCIA (`CARGA_PROGRAMACION.md` §0): lo que se crea tiene que cuadrar con lo
 * que se leyó. `reconciliarPlantilla` cuenta las marcas de la grilla y las jornadas que se van a
 * generar, y la pantalla no deja confirmar si difieren.
 */

/** Marca que cuenta como "ese día trabaja". Se acepta lo que la gente escribe de verdad. */
const MARCAS = new Set(['1', 'X', 'x', 'SI', 'SÍ', 'Si', 'Sí', 'si', 'sí', 'V', 'v', '✓']);

export interface FilaPlantilla {
  /** Tal como venía escrita en la primera columna. */
  tienda: string;
  /** Fechas marcadas, en YYYY-MM-DD y ordenadas. */
  fechas: string[];
  /** Cuántos recursos por día pidió esa tienda. Vacío = 1. */
  recursos: number;
}

export interface LecturaPlantilla {
  filas: FilaPlantilla[];
  /** Problemas que impiden usar el archivo, en palabras. */
  errores: string[];
  /** Cosas raras que NO bloquean pero conviene mirar. */
  avisos: string[];
}

/** ISO 8601: 1 = lunes … 7 = domingo. Sakamoto sobre la cadena, sin `Date`. */
const CORRIMIENTO = [0, 3, 2, 5, 0, 3, 5, 1, 4, 6, 2, 4];
export function diaIso(fecha: string): number {
  const [a, m, d] = fecha.split('-').map(Number);
  const y = m < 3 ? a - 1 : a;
  const dow = (y + Math.floor(y / 4) - Math.floor(y / 100) + Math.floor(y / 400)
    + CORRIMIENTO[m - 1] + d) % 7;
  return dow === 0 ? 7 : dow; // 0 = domingo → 7 en ISO
}

/** Todas las fechas de un rango, inclusive. Comparación lexicográfica: vale en YYYY-MM-DD. */
export function fechasDelRango(desde: string, hasta: string): string[] {
  const out: string[] = [];
  if (desde > hasta) return out;
  let f = desde;
  // Tope de seguridad: un rango absurdo no puede colgar el navegador generando millones de celdas.
  for (let i = 0; f <= hasta && i < 400; i++) {
    out.push(f);
    f = siguienteDia(f);
  }
  return out;
}

function siguienteDia(fecha: string): string {
  const [a, m, d] = fecha.split('-').map(Number);
  const dias = new Date(Date.UTC(a, m, 0)).getUTCDate();
  if (d < dias) return `${a}-${pad(m)}-${pad(d + 1)}`;
  if (m < 12) return `${a}-${pad(m + 1)}-01`;
  return `${a + 1}-01-01`;
}
const pad = (n: number): string => String(n).padStart(2, '0');

/**
 * La grilla en blanco, como matriz de celdas lista para escribir a Excel.
 *
 * Encabezado con la fecha completa (`2026-08-15`) y debajo el día de semana en letras: sin eso,
 * marcar "todos los sábados" en 31 columnas de números obliga a contar con el dedo.
 */
export function construirPlantilla(
  tiendas: { code: string; name: string }[],
  desde: string,
  hasta: string,
): (string | number)[][] {
  const fechas = fechasDelRango(desde, hasta);
  const DIAS = ['', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
  return [
    ['Tienda', 'Recursos por día', ...fechas],
    ['', '', ...fechas.map((f) => DIAS[diaIso(f)])],
    ...tiendas.map((t) => [`${t.code} — ${t.name}`, 1, ...fechas.map(() => '')]),
  ];
}

/**
 * Lee la grilla ya marcada.
 *
 * Es tolerante con la marca (1, X, sí, ✓) porque eso es lo que la gente escribe de verdad, y
 * estricto con la estructura: si el encabezado no trae fechas reconocibles no se adivina nada.
 * Adivinar acá significaría cargar jornadas en días que nadie pidió.
 */
export function leerPlantilla(matriz: unknown[][]): LecturaPlantilla {
  const errores: string[] = [];
  const avisos: string[] = [];
  if (matriz.length < 2) {
    return { filas: [], errores: ['El archivo está vacío o no tiene encabezado.'], avisos };
  }

  const encabezado = matriz[0].map((c) => texto(c));
  // Las dos primeras columnas son Tienda y Recursos; de la tercera en adelante van las fechas.
  const fechas: { col: number; fecha: string }[] = [];
  for (let c = 2; c < encabezado.length; c++) {
    const f = aFechaIso(encabezado[c]);
    if (f !== null) fechas.push({ col: c, fecha: f });
  }
  if (fechas.length === 0) {
    return {
      filas: [],
      errores: ['El encabezado no tiene ninguna fecha reconocible (se esperan columnas AAAA-MM-DD).'],
      avisos,
    };
  }

  const filas: FilaPlantilla[] = [];
  const vistas = new Set<string>();
  for (let r = 1; r < matriz.length; r++) {
    const fila = matriz[r] ?? [];
    const tienda = texto(fila[0]).trim();
    if (tienda === '') continue;                       // la fila de días de semana y las vacías
    if (tienda.toLowerCase() === 'tienda') continue;   // encabezado repetido

    if (vistas.has(tienda.toLowerCase())) {
      errores.push(`La tienda "${tienda}" aparece dos veces. Deja una sola fila por tienda.`);
      continue;
    }
    vistas.add(tienda.toLowerCase());

    const crudoRecursos = texto(fila[1]).trim();
    const recursos = crudoRecursos === '' ? 1 : Number(crudoRecursos);
    if (!Number.isInteger(recursos) || recursos < 1) {
      errores.push(`"${tienda}": «${crudoRecursos}» no es un número de recursos válido.`);
      continue;
    }

    const marcadas = fechas.filter(({ col }) => MARCAS.has(texto(fila[col]).trim())).map((x) => x.fecha);
    const sinReconocer = fechas.filter(({ col }) => {
      const v = texto(fila[col]).trim();
      return v !== '' && !MARCAS.has(v);
    });
    for (const s of sinReconocer) {
      // Aviso y no error: puede ser una nota al margen. Pero se nombra, porque si era una marca
      // mal escrita esa tienda se queda sin ese día y nadie lo nota hasta el cierre.
      avisos.push(
        `"${tienda}" tiene «${texto(fila[s.col]).trim()}» el ${s.fecha}: no se entiende como marca `
        + 'y ese día NO se va a cargar. Usa 1, X o Sí.',
      );
    }
    if (marcadas.length === 0) {
      avisos.push(`"${tienda}" no tiene ningún día marcado: se omite.`);
      continue;
    }
    filas.push({ tienda, fechas: marcadas.sort(), recursos });
  }

  if (filas.length === 0 && errores.length === 0) {
    errores.push('Ninguna tienda tiene días marcados.');
  }
  return { filas, errores, avisos };
}

const texto = (v: unknown): string => (v === null || v === undefined ? '' : String(v));

/**
 * Convierte el encabezado a YYYY-MM-DD. Acepta la cadena ISO y el número de serie de Excel, que
 * es lo que aparece cuando alguien reescribe la celda y Excel la convierte a fecha.
 */
export function aFechaIso(valor: string): string | null {
  const v = valor.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
  if (/^\d{5}$/.test(v)) {
    // Serie de Excel: día 1 = 1900-01-01, con el bug del 29-feb-1900 que Excel arrastra.
    const ms = (Number(v) - 25569) * 86400000;
    const d = new Date(ms);
    if (Number.isNaN(d.getTime())) return null;
    return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
  }
  const dmy = v.match(/^(\d{1,2})[/\-](\d{1,2})[/\-](\d{4})$/);
  if (dmy) return `${dmy[3]}-${pad(Number(dmy[2]))}-${pad(Number(dmy[1]))}`;
  return null;
}

export interface ReconciliacionPlantilla {
  /** Marcas contadas en la grilla (tienda × día). */
  diasTienda: number;
  /** Jornadas que se van a crear: cada día cuenta tantas veces como recursos pida. */
  jornadas: number;
  tiendas: number;
  /** Días de semana observados por tienda, para el `weekdays` del horario. */
  porTienda: { tienda: string; dias: number; jornadas: number; weekdays: number[] }[];
}

/**
 * Qué va a quedar en la base si se confirma. Es el número contra el que se reconcilia.
 *
 * ⚠️ Las jornadas salen de las FECHAS MARCADAS, no de expandir los días de semana sobre el rango.
 * Es la diferencia que importa: si una tienda trabaja el sábado 15 y el 29 pero no el 22, expandir
 * "sábados" crearía una jornada el 22 que nadie pidió — y esa jornada entra al cálculo de pago. El
 * `weekdays` del horario queda como descripción del patrón; la verdad de qué días son, es la
 * grilla.
 */
export function reconciliarPlantilla(filas: FilaPlantilla[]): ReconciliacionPlantilla {
  const porTienda = filas.map((f) => ({
    tienda: f.tienda,
    dias: f.fechas.length,
    jornadas: f.fechas.length * f.recursos,
    weekdays: [...new Set(f.fechas.map(diaIso))].sort((a, b) => a - b),
  }));
  return {
    diasTienda: porTienda.reduce((s, t) => s + t.dias, 0),
    jornadas: porTienda.reduce((s, t) => s + t.jornadas, 0),
    tiendas: porTienda.length,
    porTienda,
  };
}
